import { useEffect, useState, useRef } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { useLiveEvents, toastEvent } from "@/lib/live";
import { Truck, HandCoins, GasPump, Receipt, Wrench, ChartPie, FileText, MapTrifold, ChatCircleText, SignOut, CloudSlash, CloudCheck, CreditCard, Broadcast, Crown, DeviceMobile, UsersThree, ShareNetwork, Handshake, Warning, UserCircle, Download, CurrencyDollar, Megaphone, MapPinArea, Timer } from "@phosphor-icons/react";
import PlanSelectDialog from "@/components/PlanSelectDialog";
import InstallAppModal from "@/components/InstallAppModal";
import InviteDriverModal from "@/components/InviteDriverModal";
import PWAUpdateBanner from "@/components/PWAUpdateBanner";
import { AlertsBell } from "@/components/AlertsBell";
// note: Storefront comes from phosphor-icons; already available in this bundle
import { Storefront } from "@phosphor-icons/react";

const NAV = [
  { to: "veiculos", label: "Veículos", icon: Truck, testid: "tab-vehicles" },
  { to: "clientes", label: "Clientes", icon: UsersThree, testid: "tab-clients" },
  { to: "fretes", label: "Fretes", icon: HandCoins, testid: "tab-trips" },
  { to: "acerto", label: "Acerto Final", icon: Handshake, testid: "tab-acerto" },
  { to: "abastecimentos", label: "Abastec.", icon: GasPump, testid: "tab-fueling" },
  { to: "fornecedores", label: "Fornecedores", icon: Storefront, testid: "tab-suppliers" },
  { to: "despesas", label: "Despesas", icon: Receipt, testid: "tab-expenses" },
  { to: "manutencao", label: "Manutenção", icon: Wrench, testid: "tab-maintenance" },
  { to: "analise", label: "Análise", icon: ChartPie, testid: "tab-analysis" },
  { to: "painel-dono", label: "Painel do Dono", icon: CurrencyDollar, testid: "tab-owner" },
  { to: "minha-area", label: "Minha Área", icon: HandCoins, testid: "tab-my-driver" },
  { to: "marketing", label: "Marketing", icon: Megaphone, testid: "tab-marketing" },
  { to: "relatorios", label: "Relatórios", icon: FileText, testid: "tab-reports" },
  { to: "gps", label: "GPS e Rastreamento", icon: MapTrifold, testid: "tab-gps" },
  { to: "cercas", label: "Cercas", icon: MapPinArea, testid: "tab-geofences" },
  { to: "estadias", label: "Estadias", icon: Timer, testid: "tab-detentions" },
  { to: "recibos", label: "Recibos", icon: FileText, testid: "tab-receipts" },
  { to: "chat", label: "Chat", icon: ChatCircleText, testid: "tab-chat" },
  { to: "meu-plano", label: "Meu Plano", icon: Crown, testid: "tab-plan" },
  { to: "perfil", label: "Perfil", icon: UserCircle, testid: "tab-profile" },
  { to: "baixar-app", label: "Baixar App", icon: Download, testid: "tab-download" },
];

export default function AppShell({ basePath }) {
  const { user, logout, setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const fileRef = useRef(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [planOpen, setPlanOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => {
    const u = () => setOnline(true); const d = () => setOnline(false);
    window.addEventListener("online", u); window.addEventListener("offline", d);
    return () => { window.removeEventListener("online", u); window.removeEventListener("offline", d); };
  }, []);

  // Real-time events (toasts only from other users)
  useLiveEvents((evt) => {
    if (evt.actor?.id === user?.id) return;
    toastEvent(evt);
    // Browser Push Notification for arrivals (works even when tab is background)
    if (evt.entity === "trip" && evt.action === "arrived") {
      const title = "🚛 Motorista chegou ao destino";
      const body = `${evt.data.driver_name} chegou em ${evt.data.destination || "destino"}`;
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        try { new Notification(title, { body, tag: `arrival-${evt.data.id}`, icon: "/icon-192.png" }); } catch {}
      }
      toast.success(`${title} — ${body}`, { duration: 8000 });
    }
    // Broadcast on window so pages can refresh their queries
    window.dispatchEvent(new CustomEvent("live-event", { detail: evt }));
  });

  // Ask permission for push notifications (all roles, once)
  useEffect(() => {
    if (user && typeof Notification !== "undefined" && Notification.permission === "default") {
      // Delay to avoid blocking initial render
      const t = setTimeout(() => { try { Notification.requestPermission(); } catch {} }, 2500);
      return () => clearTimeout(t);
    }
  }, [user?.id]);

  const showPush = (title, body, tag) => {
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        // eslint-disable-next-line no-new
        new Notification(title, { body, tag, icon: "/logo192.png", badge: "/logo192.png" });
      }
    } catch {}
  };

  // Check revision alerts periodically and notify (all roles)
  const [revisionAlerts, setRevisionAlerts] = useState([]);
  const [partPredictions, setPartPredictions] = useState([]);
  useEffect(() => {
    let alive = true;
    const notifiedKey = "revision_alerts_notified_v2";
    const partNotifiedKey = "part_predictions_notified_v1";
    const notified = JSON.parse(sessionStorage.getItem(notifiedKey) || "[]");
    const partNotified = JSON.parse(sessionStorage.getItem(partNotifiedKey) || "[]");
    const check = async () => {
      try {
        const [alertsRes, predsRes] = await Promise.all([
          api.get("/vehicles/revision-alerts").catch(() => ({ data: [] })),
          api.get("/analysis/part-predictions").catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        setRevisionAlerts(alertsRes.data);
        setPartPredictions(predsRes.data);
        // Revision alerts
        for (const a of alertsRes.data) {
          const key = `rev-${a.vehicle_id}-${a.status}`;
          if (!notified.includes(key)) {
            const title = a.status === "overdue" ? "🚨 Revisão ATRASADA" : a.status === "critical" ? "⚠️ Revisão URGENTE" : "🔔 Revisão se aproximando";
            const msg = a.status === "overdue"
              ? `${a.plate_truck} passou ${Math.abs(a.km_remaining).toLocaleString("pt-BR")} km da revisão programada`
              : `${a.plate_truck}: faltam ${a.km_remaining.toLocaleString("pt-BR")} km para revisão`;
            toast.warning(msg, { duration: 10000, id: key });
            showPush(title, msg, key);
            notified.push(key);
          }
        }
        sessionStorage.setItem(notifiedKey, JSON.stringify(notified));
        // Part predictions — only push notify for CRITICAL and OVERDUE
        for (const p of predsRes.data) {
          if (p.status !== "critical" && p.status !== "overdue") continue;
          const key = `part-${p.vehicle_id}-${p.part_name}-${p.status}`;
          if (!partNotified.includes(key)) {
            const title = p.status === "overdue" ? `🚨 ${p.part_name}: hora de trocar!` : `⚠️ ${p.part_name}: quase na hora`;
            const msg = p.status === "overdue"
              ? `${p.plate_truck}: rodou ${p.km_since_last.toLocaleString("pt-BR")} km — passou da média de ${p.avg_interval.toLocaleString("pt-BR")} km`
              : `${p.plate_truck}: rodou ${p.km_since_last.toLocaleString("pt-BR")} de ${p.avg_interval.toLocaleString("pt-BR")} km. Faltam ${p.km_remaining.toLocaleString("pt-BR")} km`;
            toast.warning(msg, { duration: 12000, id: key });
            showPush(title, msg, key);
            partNotified.push(key);
          }
        }
        sessionStorage.setItem(partNotifiedKey, JSON.stringify(partNotified));
      } catch {}
    };
    check();
    const interval = setInterval(check, 5 * 60 * 1000); // every 5 min
    return () => { alive = false; clearInterval(interval); };
  }, [user?.company_id]);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${API}/files/${data.id}?auth=${localStorage.getItem("token")}`;
      await api.post("/auth/photo", { photo_url: url });
      setUser({ ...user, photo_url: url });
      localStorage.setItem("user", JSON.stringify({ ...user, photo_url: url }));
      toast.success("Foto atualizada");
    } catch { toast.error("Falha no upload"); }
  };

  const startCheckout = () => setPlanOpen(true);

  const trialDaysLeft = user?.trial_ends_at ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000)) : null;
  const showTrialAlert = (user?.role === "company" || user?.role === "autonomo") && !user?.subscription_active && trialDaysLeft !== null && trialDaysLeft <= 2;

  return (
    <div className="min-h-screen bg-background">
      <PWAUpdateBanner />
      {/* Trial urgent banner */}
      {showTrialAlert && (
        <div
          data-testid="trial-urgent-banner"
          className={`w-full text-center py-2.5 px-4 text-sm font-semibold shadow-md ${
            trialDaysLeft <= 0
              ? "bg-red-600 text-white"
              : trialDaysLeft === 1
                ? "bg-orange-500 text-white"
                : "bg-yellow-400 text-slate-900"
          }`}
        >
          <div className="max-w-[1600px] mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
            <span className="flex items-center gap-1.5">
              <Warning size={16} weight="fill" />
              {trialDaysLeft <= 0
                ? "Seu trial acabou hoje!"
                : trialDaysLeft === 1
                  ? "Falta só 1 dia pro seu trial acabar!"
                  : `Faltam ${trialDaysLeft} dias pro seu trial acabar`}
              <span className="hidden sm:inline"> — Assine agora e não perca seus dados.</span>
            </span>
            <button
              onClick={startCheckout}
              data-testid="trial-banner-cta"
              className="bg-white hover:bg-slate-100 text-slate-900 font-extrabold px-4 py-1.5 rounded-md text-xs uppercase tracking-wide transition-colors flex items-center gap-1.5 whitespace-nowrap"
            >
              <CreditCard size={14} weight="fill" /> Assinar agora
            </button>
          </div>
        </div>
      )}
      {/* Road banner (decorative) */}
      <div
        data-testid="road-banner"
        aria-hidden
        className="w-full h-24 md:h-32 lg:h-36 relative overflow-hidden border-b border-blue-100"
        style={{
          backgroundImage: "url(/road-banner.png)",
          backgroundSize: "cover",
          backgroundPosition: "center 42%",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* subtle white gradient overlay so the header below reads clearly */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/10 to-white/60 dark:to-slate-950/60" />
      </div>
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-white/85 dark:bg-slate-950/85 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-3 md:px-6 h-16 flex items-center justify-between gap-2 md:gap-4">
          <div className="flex items-center gap-3 shrink-0">
            <Logo size={36} />
          </div>
          <div className="flex items-center gap-1.5 md:gap-3 min-w-0">
            <Badge data-testid="online-status" variant="outline" className={`gap-1 ${online ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-amber-300 text-amber-700 bg-amber-50"}`}>
              {online ? <CloudCheck size={14} /> : <CloudSlash size={14} />} <span className="hidden sm:inline">{online ? "Online" : "Offline"}</span>
            </Badge>
            {online && <Badge variant="outline" className="gap-1 border-blue-300 text-blue-700 bg-blue-50 hidden sm:flex" data-testid="live-indicator"><Broadcast size={14} weight="fill" className="animate-pulse" /> Ao vivo</Badge>}
            {(user?.role === "company" || user?.role === "autonomo") && (
              <Badge variant={user.subscription_active ? "default" : "secondary"} className={user.subscription_active ? "bg-blue-600" : ""}>
                {user.subscription_active ? "Assinante" : `Trial: ${trialDaysLeft ?? "?"}d`}
              </Badge>
            )}
            {(user?.role === "company" || user?.role === "autonomo") && !user.subscription_active && (
              <Button size="sm" onClick={startCheckout} data-testid="btn-subscribe" className="bg-yellow-500 hover:bg-yellow-600 hover:text-black text-black font-semibold gap-1.5">
                <CreditCard size={16} /> Assinar
              </Button>
            )}
            <AlertsBell />
            <input type="file" ref={fileRef} accept="image/*" hidden onChange={onPhoto} data-testid="photo-input" />
            <button data-testid="header-avatar" onClick={() => fileRef.current?.click()} className="rounded-full ring-2 ring-blue-600 hover:ring-blue-700 transition-colors">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.photo_url} alt="foto" />
                <AvatarFallback className="bg-blue-600 text-white text-xs font-bold">
                  {(user?.name || user?.company_name || "U").slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </button>
            <Button variant="ghost" size="sm" onClick={() => setInstallOpen(true)} data-testid="btn-install-app" className="gap-1 hidden md:inline-flex"><DeviceMobile size={16} weight="duotone" /> Instalar</Button>
            {user?.role === "company" && (
              <Button variant="ghost" size="sm" onClick={() => setInviteOpen(true)} data-testid="btn-invite-driver" className="gap-1 hidden md:inline-flex text-blue-700 hover:bg-blue-50 hover:text-blue-800">
                <ShareNetwork size={16} weight="duotone" /> Convidar motorista
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={logout} data-testid="btn-logout" className="gap-1 px-2 sm:px-3"><SignOut size={16} /> <span className="hidden sm:inline">Sair</span></Button>
          </div>
        </div>
        {/* Tabs Nav */}
        <nav className="border-t border-border/60 bg-white dark:bg-slate-950">
          <div className="max-w-[1600px] mx-auto px-2 md:px-6 flex overflow-x-auto no-scrollbar gap-1">
            {NAV.filter((n) => {
              if (user?.role === "autonomo" && n.to === "chat") return false;
              if (user?.role === "driver" && n.to === "meu-plano") return false;
              if (user?.role === "driver" && n.to === "painel-dono") return false;
              if (user?.role === "driver" && (n.to === "cercas" || n.to === "estadias")) return false;
              if (user?.role !== "autonomo" && n.to === "recibos") return false;
              if (user?.role === "company" && n.to === "minha-area") return false;
              if (n.to === "marketing" && !user?.is_admin) return false;
              return true;
            }).map(({ to, label, icon: Icon, testid }) => {
              const urgentParts = partPredictions.filter((p) => p.status === "critical" || p.status === "overdue").length;
              const badgeCount = to === "manutencao" ? (revisionAlerts.length + urgentParts) : 0;
              return (
              <NavLink key={to} to={`${basePath}/${to}`} data-testid={testid}
                className={({ isActive }) => `flex items-center gap-2 px-3 md:px-4 py-3 border-b-2 text-sm whitespace-nowrap transition-colors relative ${isActive ? "border-blue-600 text-blue-700 font-semibold" : "border-transparent text-slate-600 hover:text-blue-700 hover:border-blue-200"}`}>
                <Icon size={16} weight="duotone" /> {label}
                {badgeCount > 0 && (
                  <span data-testid={`tab-badge-${to}`} className="absolute -top-0.5 -right-0.5 md:right-1 md:top-1 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow-sm animate-pulse">
                    {badgeCount}
                  </span>
                )}
              </NavLink>
            );})}
          </div>
        </nav>
      </header>

      {/* Content */}
      <main className="max-w-[1600px] mx-auto p-4 md:p-8">
        <Outlet />
      </main>
      <PlanSelectDialog open={planOpen} onOpenChange={setPlanOpen} />
      <InstallAppModal forceOpen={installOpen} onOpenChange={setInstallOpen} />
      <InviteDriverModal open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}
